/*
 * assets/header-interactions.js
 * ------------------------------------------------------------------
 * Progressive-enhancement JS for the site header:
 *  - Search: focuses the input on open, closes on ×, Escape, or an
 *    outside click. The underlying <details>/<summary> and <form
 *    method="get" action="{{ routes.search_url }}"> already work with
 *    JS disabled — this file only adds the polish on top.
 *  - Cart drawer: intercepts clicks on the bag icon to open a drawer
 *    instead of navigating to /cart, and updates quantities/removals
 *    through Shopify's real AJAX Cart API (never fakes cart state).
 *    If JS fails to load, the bag icon still works as a plain link to
 *    the native /cart page.
 *  - THEME EDITOR REINIT (bugfix): all of the above is re-run whenever
 *    the Theme Editor swaps a section's markup, not just once on page
 *    load — see the "shopify:section:load" listener at the bottom for
 *    why this was needed and what it was silently breaking before.
 *  - window.UnnamedCart: a small public hook (open/refresh/sectionId)
 *    exposed from initCartDrawer() so OTHER scripts on the site (e.g.
 *    assets/product-form.js on the product page) can open and refresh
 *    this SAME cart drawer after their own AJAX Cart API calls, rather
 *    than building a second, parallel cart UI. Re-exposed every time
 *    initCartDrawer() re-runs (see initAll()) so it always points at
 *    whichever drawer instance is currently in the DOM.
 * ------------------------------------------------------------------
 */
(function () {
  "use strict";

  document.addEventListener("DOMContentLoaded", initAll);

  /*
   * THEME EDITOR BUGFIX — "the bar jumps/glitches to the top while I'm
   * editing settings": the Theme Editor doesn't reload the page when you
   * change a setting — it swaps just that section's HTML in over AJAX
   * (Shopify fires a "shopify:section:load" event on the section's
   * wrapper when this happens). Every function below used to run only
   * once, on the page's original DOMContentLoaded:
   *   - initStickyHeaderOffset() had measured the announcement bar +
   *     header and written the result as inline CSS custom properties
   *     (--sticky-header-offset / --fixed-content-offset) onto the
   *     ORIGINAL header/announcement DOM elements. The moment you edit
   *     any setting on the header or announcement-bar section, Shopify
   *     replaces those elements with brand-new ones that have never had
   *     those properties set — so the header's `transform:
   *     translateY(var(--sticky-header-offset, 0px))` falls back to 0
   *     and the header instantly snaps to the very top of the viewport.
   *     That's the "shoots up" glitch: not a rendering bug, but stale
   *     JS state pointing at DOM nodes that no longer exist.
   *   - initSearch()/initCartDrawer() had the exact same problem for
   *     the search toggle and cart-drawer button: after any header edit
   *     in the editor, those stopped responding to clicks until a full
   *     page reload, because their listeners were bound to the old,
   *     now-detached elements.
   * Fix: re-run all three on every "shopify:section:load" event, not
   * only once. Cheap and safe — each function already no-ops if the
   * element it needs isn't present, so reinitializing after an
   * unrelated section's edit costs nothing.
   */
  function initAll() {
    initSearch();
    initCartDrawer();
    initStickyHeaderOffset();
  }

  document.addEventListener("shopify:section:load", initAll);

  /* --------------------- Sticky header offset --------------------- */
  /*
   * The header is `position: fixed` (see the root-cause comment in
   * sections/site-header.liquid — a Shopify section wrapper is only ever
   * as tall as its own content, so `position: sticky` can never hold a
   * single-row header section: it gets released and scrolls away the
   * moment the wrapper itself leaves the viewport). Being fixed, the
   * header no longer sits in normal document flow below the
   * announcement bar, so this keeps that same visual relationship:
   *  - at the top of the page, the header sits visually right below the
   *    (non-sticky) announcement bar, exactly as before this fix;
   *  - as the user scrolls, the header rises smoothly with the
   *    announcement bar until it reaches the very top of the viewport;
   *  - past that point, the header stays pinned at the top and the
   *    announcement bar's height is never reserved again.
   * Implemented as a single CSS custom property (--sticky-header-offset)
   * consumed by a `transform: translateY(...)` in the header's own CSS,
   * so this never fights with any other positioning logic. No-op (and
   * harmless) when the "Sticky header" setting is off, since that state
   * never renders the [data-sticky-header] attribute in the first place.
   *
   * THEME EDITOR BUGFIX: this function is now called again on every
   * "shopify:section:load" event (see initAll() above), so it must
   * clean up its OWN previous scroll/resize listeners first — otherwise
   * every edit in the Theme Editor would stack another pair of
   * listeners on top of the old ones (still attached to now-detached
   * elements), each doing pointless work forever. stickyListeners keeps
   * track of exactly what was attached last time so it can be removed
   * before attaching the fresh set.
   */
  var stickyListeners = [];

  function initStickyHeaderOffset() {
    stickyListeners.forEach(function (entry) {
      window.removeEventListener(entry.type, entry.handler);
    });
    stickyListeners = [];

    var header = document.querySelector("[data-sticky-header]");
    if (!header) return;

    var announcement = document.querySelector(".announcement-bar-section");
    if (!announcement) {
      header.style.setProperty("--sticky-header-offset", "0px");
      return;
    }

    /* HEADER BEHAVIOUR CORRECTION — homepage vs other pages:
     * sections/announcement-bar.liquid renders this bar as `position:
     * fixed` on every page except the homepage (index) — see that
     * file's is_home Liquid variable. That single CSS fact is the only
     * signal this needs: no separate "which page" logic is duplicated
     * here, it just reads the bar's own computed position.
     *  - Fixed (non-homepage): the bar itself no longer scrolls away, so
     *    the header's offset is simply the bar's real, dynamically
     *    measured height, permanently — re-measured on resize only.
     *  - Not fixed (homepage): unchanged from before — the bar scrolls
     *    away normally and the header's offset shrinks from the bar's
     *    height down to 0 as the user scrolls past it.
     */
    var announcementIsFixed = window.getComputedStyle(announcement).position === "fixed";

    if (announcementIsFixed) {
      /* HEADER BEHAVIOUR CORRECTION follow-up — content overlap fix:
       * on these pages the announcement bar + header are both fixed,
       * permanently occupying real screen space at the top. The page
       * content below them (e.g. a collection's title/category-tabs/
       * filter row) still starts at the very top of the document flow
       * and was rendering hidden behind that fixed stack. Rather than
       * hardcoding a pixel value or duplicating page-type checks, this
       * reuses the same two measurements already taken for the header's
       * own offset and publishes their sum as a second CSS custom
       * property, --fixed-content-offset, on the root element. Layout
       * consumes it as this project's own #MainContent padding-top (see
       * layout/theme.liquid) so any page's content is pushed down by
       * exactly the real, dynamically measured height of the fixed
       * announcement+header stack — never on the homepage, where this
       * branch never runs and the property stays unset (falls back to
       * 0px, so the header keeps floating over the hero unchanged). */
      var updateFixedOffset = function () {
        var barHeight = announcement.offsetHeight;
        header.style.setProperty("--sticky-header-offset", barHeight + "px");
        var stackHeight = barHeight + header.offsetHeight;
        document.documentElement.style.setProperty("--fixed-content-offset", stackHeight + "px");
      };
      updateFixedOffset();
      window.addEventListener("resize", updateFixedOffset);
      stickyListeners.push({ type: "resize", handler: updateFixedOffset });
      return;
    }

    var ticking = false;

    var update = function () {
      var barHeight = announcement.offsetHeight;
      var offset = Math.max(0, barHeight - window.scrollY);
      header.style.setProperty("--sticky-header-offset", offset + "px");
      ticking = false;
    };

    var onScroll = function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", update);
    stickyListeners.push({ type: "scroll", handler: onScroll });
    stickyListeners.push({ type: "resize", handler: update });
  }

  /* ------------------------------ Search ------------------------------ */
  /*
   * THEME EDITOR BUGFIX: the outside-click-closes-search behaviour used
   * to add one "click" listener on `document` per search instance,
   * every time initSearch() ran. Re-running this on every Theme Editor
   * section reload (see initAll() above) would otherwise stack up a new
   * document-level listener on every single edit, forever. Fixed by
   * binding that one listener to `document` exactly once (guarded by
   * searchOutsideClickBound) and having it look up whichever search
   * <details> happen to be open at click time, instead of closing over
   * a specific (and potentially stale) details element.
   */
  var searchOutsideClickBound = false;

  function initSearch() {
    var detailsList = document.querySelectorAll(".site-header__search-details");

    detailsList.forEach(function (details) {
      var summary = details.querySelector("summary");
      var input = details.querySelector("[data-search-input]");
      var closeBtn = details.querySelector("[data-search-close]");

      details.addEventListener("toggle", function () {
        if (summary) {
          summary.setAttribute("aria-expanded", details.open ? "true" : "false");
        }
        if (details.open && input) {
          window.requestAnimationFrame(function () {
            input.focus();
          });
        }
      });

      if (closeBtn) {
        closeBtn.addEventListener("click", function (e) {
          e.preventDefault();
          details.open = false;
          if (summary) summary.focus();
        });
      }

      details.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && details.open) {
          e.stopPropagation();
          details.open = false;
          if (summary) summary.focus();
        }
      });
    });

    if (!searchOutsideClickBound) {
      searchOutsideClickBound = true;
      document.addEventListener("click", function (e) {
        document.querySelectorAll(".site-header__search-details[open]").forEach(function (openDetails) {
          if (!openDetails.contains(e.target)) {
            openDetails.open = false;
          }
        });
      });
    }
  }

  /* ---------------------------- Cart drawer ---------------------------- */
  function initCartDrawer() {
    var drawer = document.getElementById("CartDrawer");
    if (!drawer) return;

    var openTriggers = document.querySelectorAll("[data-cart-drawer-open]");
    var closeTriggers = drawer.querySelectorAll("[data-cart-drawer-close]");
    var sectionId = drawer.getAttribute("data-section-id");
    var changeUrl = drawer.getAttribute("data-cart-change-url") || "/cart/change.js";
    var lastFocused = null;
    var closeTimer = null;

    function openDrawer() {
      lastFocused = document.activeElement;
      window.clearTimeout(closeTimer);
      drawer.hidden = false;
      window.requestAnimationFrame(function () {
        drawer.setAttribute("data-open", "true");
      });
      document.body.style.overflow = "hidden";
      var closeBtn = drawer.querySelector(".cart-drawer__close");
      if (closeBtn) closeBtn.focus();
      document.addEventListener("keydown", onKeydown);
    }

    function closeDrawer() {
      drawer.setAttribute("data-open", "false");
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeydown);
      closeTimer = window.setTimeout(function () {
        if (drawer.getAttribute("data-open") === "false") {
          drawer.hidden = true;
        }
      }, 320);
      if (lastFocused && typeof lastFocused.focus === "function") {
        lastFocused.focus();
      }
    }

    function onKeydown(e) {
      if (e.key === "Escape") {
        closeDrawer();
        return;
      }
      if (e.key === "Tab") {
        trapFocus(e);
      }
    }

    function trapFocus(e) {
      var focusable = drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    openTriggers.forEach(function (trigger) {
      trigger.addEventListener("click", function (e) {
        e.preventDefault();
        openDrawer();
      });
    });

    closeTriggers.forEach(function (trigger) {
      trigger.addEventListener("click", function (e) {
        e.preventDefault();
        closeDrawer();
      });
    });

    function refreshCart(html) {
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, "text/html");

      var newBody = doc.querySelector("[data-cart-drawer-body]");
      var currentBody = drawer.querySelector("[data-cart-drawer-body]");
      if (newBody && currentBody) {
        currentBody.innerHTML = newBody.innerHTML;
      }

      var newFooter = doc.querySelector("[data-cart-drawer-footer]");
      var currentFooter = drawer.querySelector("[data-cart-drawer-footer]");
      if (newFooter && currentFooter) {
        currentFooter.innerHTML = newFooter.innerHTML;
        currentFooter.hidden = newFooter.hasAttribute("hidden");
      }

      var newCounts = doc.querySelectorAll("[data-cart-count]");
      var currentCounts = document.querySelectorAll("[data-cart-count]");
      newCounts.forEach(function (newCount, index) {
        var currentCount = currentCounts[index];
        if (!currentCount) return;
        currentCount.textContent = newCount.textContent;
        currentCount.hidden = newCount.hasAttribute("hidden");
      });
    }

    function updateLine(key, quantity) {
      var body = { id: key, quantity: quantity };
      if (sectionId) body.sections = sectionId;

      fetch(changeUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          if (data && data.sections && sectionId && data.sections[sectionId]) {
            refreshCart(data.sections[sectionId]);
          }
        })
        .catch(function () {
          /* Never fake cart state — if the request fails, send the
             customer to the real cart page rather than leave the
             drawer showing stale quantities. */
          window.location.href = "/cart";
        });
    }

    drawer.addEventListener("click", function (e) {
      var decrease = e.target.closest("[data-cart-qty-decrease]");
      var increase = e.target.closest("[data-cart-qty-increase]");
      var remove = e.target.closest("[data-cart-remove]");
      if (!decrease && !increase && !remove) return;

      var item = e.target.closest("[data-cart-drawer-item]");
      if (!item) return;
      var key = item.getAttribute("data-key");
      var input = item.querySelector("[data-cart-qty-input]");
      var current = input ? parseInt(input.value, 10) || 0 : 0;

      if (remove) {
        updateLine(key, 0);
      } else if (decrease) {
        updateLine(key, Math.max(0, current - 1));
      } else if (increase) {
        updateLine(key, current + 1);
      }
    });

    drawer.addEventListener("change", function (e) {
      if (!e.target.matches("[data-cart-qty-input]")) return;
      var item = e.target.closest("[data-cart-drawer-item]");
      if (!item) return;
      var key = item.getAttribute("data-key");
      var quantity = Math.max(0, parseInt(e.target.value, 10) || 0);
      updateLine(key, quantity);
    });

    /*
     * window.UnnamedCart — public hook so OTHER scripts (e.g. the
     * product page's assets/product-form.js) can open/refresh THIS
     * exact cart drawer after their own Cart API calls, instead of
     * building a second, parallel cart UI. sectionId is exposed too
     * so a caller can pass it as `sections=` on its own cart API
     * request and hand the matching response fragment to refresh().
     * Re-assigned every time initCartDrawer() re-runs (page load AND
     * every Theme Editor "shopify:section:load"), so it always points
     * at whichever drawer instance is currently live in the DOM.
     */
    window.UnnamedCart = {
      open: openDrawer,
      close: closeDrawer,
      refresh: refreshCart,
      sectionId: sectionId
    };
  }
})();
